import { SearchIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Spacer,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { isTruthy } from '@llx/common';
import { debounce } from 'lodash';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  QueryMode,
  useAddUserToTeamMutation,
  useCreateTeamMutation,
  useListUsersQuery,
  useRemoveUserFromTeamMutation,
  useUpdateTeamMutation,
} from '@/@generated/codegen/API';
import { AppSpinner } from '@/ui/AppSpinner';
import { extractMessageFromUnknownError } from '@/utils/extractMessageFromUnknownError';
import { captureSentry } from '@/utils/helpers/sentryHelper';

import { AdminTeamListItemType } from '../AdminTeamList/columns';
import { SelectableUserList } from '../SelectableUserList/SelectableUserList';

type Props = {
  row: AdminTeamListItemType | null;
  closeForm: () => void;
};

type FormData = {
  teamName: string;
};

export default function CreateTeamForm({ row, closeForm }: Props) {
  const { t } = useTranslation();
  const isEdit = !!row;
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>();

  const currentMemberIds = row?.members?.edges.filter(isTruthy).map((elem) => elem.node.id) || [];
  const [currentlySelectedUserIds, setCurrentlySelectedUserIds] =
    useState<string[]>(currentMemberIds);
  const toast = useToast();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [realtimeSearchQuery, setRealtimeSearchQuery] = useState<string>('');
  const debounced = debounce(setSearchQuery, 500);
  const debouncedOnChange = useCallback(debounced, []);
  const users = useListUsersQuery({
    variables: {
      where: {
        isMocked: {
          equals: false,
        },
        isGuest: {
          equals: false,
        },
        email: {
          contains: searchQuery,
          mode: QueryMode.Insensitive,
        },
      },
    },
  });
  const [mutationCreateTeam] = useCreateTeamMutation();
  const [mutationUpdateTeam] = useUpdateTeamMutation();
  const [mutationAddUserToTeam] = useAddUserToTeamMutation();
  const [mutationRemoveUserFromTeam] = useRemoveUserFromTeamMutation();

  const onSubmit = async (formData: FormData) => {
    if (isEdit) {
      await mutationUpdateTeam({
        variables: {
          data: {
            id: row.id,
            name: {
              set: formData.teamName,
            },
          },
        },
      });
      const toRemove = currentMemberIds.filter(
        (elem) => currentlySelectedUserIds.includes(elem) === false
      );
      const toAdd = currentlySelectedUserIds.filter(
        (elem) => currentMemberIds.includes(elem) === false
      );
      const toRemovePromises = toRemove.map((userId) => {
        return mutationRemoveUserFromTeam({
          variables: {
            data: {
              teamId: row.id,
              userId,
            },
          },
        });
      });
      const toAddPromises = toAdd.map((userId) => {
        return mutationAddUserToTeam({
          variables: {
            data: {
              teamId: row.id,
              userId,
            },
          },
        });
      });
      await Promise.all([...toRemovePromises, ...toAddPromises]);
      closeForm();
      toast({
        status: 'success',
        title: t('common.success'),
        position: 'top',
      });
    } else {
      try {
        const result = await mutationCreateTeam({
          variables: {
            data: {
              name: formData.teamName,
            },
          },
        });
        const teamId = result.data?.createTeam?.id;

        if (!teamId) {
          captureSentry({
            title: 'Create Team, teamId is null',
            detail: {
              teamId,
              result,
            },
          });
          return;
        }
        //NOTE: add all the users to the team
        const promises = currentlySelectedUserIds.map((userId) => {
          return mutationAddUserToTeam({
            variables: {
              data: {
                teamId,
                userId,
              },
            },
          });
        });
        await Promise.all(promises);
        closeForm();
        toast({
          status: 'success',
          title: t('common.success'),
          position: 'top',
        });
      } catch (e: unknown) {
        console.log(e);
        toast({
          status: 'error',
          title: t('common.error'),
          description: extractMessageFromUnknownError(e),
          position: 'top',
        });
      }
    }
  };

  const handleSelect = useCallback(
    (userId: string, newState: boolean) => {
      if (newState) {
        setCurrentlySelectedUserIds((prev) => [...prev, userId]);
      } else {
        setCurrentlySelectedUserIds((prev) => prev.filter((elem) => elem !== userId));
      }
    },
    [currentlySelectedUserIds]
  );

  if (!users.data) {
    return <AppSpinner />;
  }

  const usersList = users.data.listUsers.edges.map((el) => el.node);

  return (
    <form
      onSubmit={(e) => {
        void handleSubmit(onSubmit)(e);
      }}
    >
      <fieldset disabled={isSubmitting}>
        <VStack gap="10px" pb="10px">
          <FormControl isRequired isInvalid={Boolean(errors.teamName)}>
            <FormLabel fontSize="sm" fontWeight="normal" mb="1">
              {t('admin.teams.form.teamName.label')}
            </FormLabel>
            <Input
              type="text"
              {...register('teamName', { required: true })}
              placeholder="Kit 01"
              defaultValue={(row && row.name) || ''}
            />

            {errors.teamName && (
              <FormHelperText color="red">{t('admin.teams.form.teamName.error')}</FormHelperText>
            )}
          </FormControl>

          <FormControl>
            <FormLabel fontSize="sm" fontWeight="normal" mb="1">
              {t('admin.teams.form.members.label')}
            </FormLabel>
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <Icon as={SearchIcon} color="gray" />
              </InputLeftElement>
              <Input
                value={realtimeSearchQuery}
                onChange={(e) => {
                  setRealtimeSearchQuery(e.target.value);
                  debouncedOnChange(e.target.value);
                }}
                placeholder="Search"
              />
            </InputGroup>
            <Box justifyContent="left" w="full" ml={2} mt={4}>
              <SelectableUserList
                userList={usersList}
                onSelectionChangeUser={handleSelect}
                selectedUsersIds={currentlySelectedUserIds}
                // NOTE: can not add a user to a team if they are already in a team
                disabledUserList={usersList
                  .filter((elem) => !!elem.teamId && elem.teamId !== row?.id)
                  .map((elem) => elem.id)}
              />
            </Box>
          </FormControl>

          <Spacer h="5px" />

          <Button
            type="submit"
            p="2"
            width="full"
            background="blue.400"
            color="white"
            variant="solid"
            isDisabled={isSubmitting}
            isLoading={isSubmitting}
          >
            {isEdit ? t('common.update') : t('common.create')}
          </Button>
        </VStack>
      </fieldset>
    </form>
  );
}
